import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowLeft } from "lucide-react";
import { Navigation, Footer, ScriptureBlock } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

export default function SellSubmit() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    property_address: "",
    city: "",
    state: "",
    zip_code: "",
    county: "",
    apn: "",
    acres: "",
    utilities: {
      water: "unknown",
      sewer: "unknown",
      gas: "unknown",
      electric: "unknown"
    },
    known_issues: "",
    timeline_preference: "",
    goal: "",
    owner_confirmed: false,
    transparency_understood: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUtilityChange = (utility, value) => {
    setFormData(prev => ({
      ...prev,
      utilities: { ...prev.utilities, [utility]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.owner_confirmed || !formData.transparency_understood) {
      toast.error("Please confirm both checkboxes to proceed.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads/seller-property`, formData);
      setSubmitted(true);
      toast.success("Property submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="sell-submit-success">
        <Navigation mode="seller" />
        <div className="pt-24 pb-16 px-6 min-h-screen flex items-center justify-center">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-[#0F0F10] rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
              Property Submitted
            </h1>
            <p className="text-[#57534E] text-lg mb-8">
              Thank you for trusting us with your property. We'll review your submission and contact you within 24-48 hours.
            </p>
            <ScriptureBlock
              verse="We will pray for wisdom and for your best outcome under God's will."
              reference="Our Promise"
              className="mb-8"
            />
            <Link to="/seller">
              <Button variant="outline" className="font-medium">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Seller Mode
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="sell-submit-page">
      <SEO 
        title="Submit Your Property"
        description="Submit your property to Steward Lots. Sell your land with integrity, full disclosure, and transparent compensation. No pressure, no manipulation."
        path="/sell/submit"
      />
      <Navigation mode="seller" />

      <div className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <Link to="/seller" className="text-[#57534E] hover:text-[#1C1917] inline-flex items-center gap-2 mb-6">
              <ArrowLeft className="w-4 h-4" />
              Back to Seller Mode
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
              Submit Your Property
            </h1>
            <p className="text-[#57534E] text-lg">
              Tell us about your property. All information is kept confidential.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="property-form">
            {/* Contact Information */}
            <div className="space-y-6">
              <h2 className="font-serif text-xl text-[#1C1917] pb-4 border-b border-[#E7E5E4]">
                Contact Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Property Information */}
            <div className="space-y-6">
              <h2 className="font-serif text-xl text-[#1C1917] pb-4 border-b border-[#E7E5E4]">
                Property Information
              </h2>
              
              <div>
                <Label htmlFor="property_address">Property Address / Nearest Address *</Label>
                <Input
                  id="property_address"
                  name="property_address"
                  value={formData.property_address}
                  onChange={handleChange}
                  required
                  data-testid="input-address"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}>
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleChange}
                    required
                    data-testid="input-zip"
                  />
                </div>
                <div>
                  <Label htmlFor="county">County *</Label>
                  <Input
                    id="county"
                    name="county"
                    value={formData.county}
                    onChange={handleChange}
                    required
                    data-testid="input-county"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="apn">APN (Assessor Parcel Number)</Label>
                  <Input
                    id="apn"
                    name="apn"
                    value={formData.apn}
                    onChange={handleChange}
                    placeholder="Optional"
                    data-testid="input-apn"
                  />
                </div>
                <div>
                  <Label htmlFor="acres">Acres</Label>
                  <Input
                    id="acres"
                    name="acres"
                    value={formData.acres}
                    onChange={handleChange}
                    placeholder="Optional"
                    data-testid="input-acres"
                  />
                </div>
              </div>
            </div>

            {/* Utilities */}
            <div className="space-y-6">
              <h2 className="font-serif text-xl text-[#1C1917] pb-4 border-b border-[#E7E5E4]">
                Utilities (Optional)
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {["water", "sewer", "gas", "electric"].map((utility) => (
                  <div key={utility}>
                    <Label htmlFor={utility} className="capitalize">{utility}</Label>
                    <Select 
                      value={formData.utilities[utility]} 
                      onValueChange={(value) => handleUtilityChange(utility, value)}
                    >
                      <SelectTrigger data-testid={`select-${utility}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unknown">Unknown</SelectItem>
                        <SelectItem value="at_street">At Street</SelectItem>
                        <SelectItem value="nearby">Nearby</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-6">
              <h2 className="font-serif text-xl text-[#1C1917] pb-4 border-b border-[#E7E5E4]">
                Additional Information
              </h2>
              
              <div>
                <Label htmlFor="known_issues">Known Issues</Label>
                <Textarea
                  id="known_issues"
                  name="known_issues"
                  value={formData.known_issues}
                  onChange={handleChange}
                  placeholder="Any known issues with the property (liens, easements, access, etc.)"
                  rows={3}
                  data-testid="input-issues"
                />
              </div>

              <div>
                <Label htmlFor="timeline_preference">Timeline Preference</Label>
                <Select value={formData.timeline_preference} onValueChange={(value) => setFormData(prev => ({ ...prev, timeline_preference: value }))}>
                  <SelectTrigger data-testid="select-timeline">
                    <SelectValue placeholder="Select your preferred timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asap">As soon as possible</SelectItem>
                    <SelectItem value="1-3_months">1-3 months</SelectItem>
                    <SelectItem value="3-6_months">3-6 months</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="goal">What's Most Important to You?</Label>
                <Select value={formData.goal} onValueChange={(value) => setFormData(prev => ({ ...prev, goal: value }))}>
                  <SelectTrigger data-testid="select-goal">
                    <SelectValue placeholder="Select your primary goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="speed">Speed - Close quickly</SelectItem>
                    <SelectItem value="certainty">Certainty - Know it will close</SelectItem>
                    <SelectItem value="max_net">Maximum Net - Get the best price</SelectItem>
                    <SelectItem value="simple">Simplicity - Easy process</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Confirmations */}
            <div className="space-y-4 bg-[#F5F5F4] p-6 border border-[#E7E5E4]">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="owner_confirmed"
                  checked={formData.owner_confirmed}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, owner_confirmed: checked }))}
                  data-testid="checkbox-owner"
                />
                <Label htmlFor="owner_confirmed" className="text-sm cursor-pointer">
                  I confirm I am the owner or authorized representative of this property. *
                </Label>
              </div>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="transparency_understood"
                  checked={formData.transparency_understood}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, transparency_understood: checked }))}
                  data-testid="checkbox-transparency"
                />
                <Label htmlFor="transparency_understood" className="text-sm cursor-pointer">
                  I understand Steward Lots operates transparently and will disclose compensation in writing. *
                </Label>
              </div>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              size="lg" 
              className="w-full bg-[#0F0F10] hover:bg-[#1C1917]"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? "Submitting..." : "Submit Property"}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
